import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Megaphone,
  Plus,
  Trash2,
  AlertTriangle,
  Info,
  Calendar,
  Tag,
  Send,
  User,
  Users,
  Search,
  X,
  UserCheck,
} from "lucide-react";
import { useAnnouncements } from "../hooks";

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "policy", label: "Policy Update" },
  { value: "event", label: "Event" },
  { value: "hr_update", label: "HR Update" },
  { value: "holiday", label: "Holiday" },
  { value: "achievement", label: "Achievement" },
];

const PRIORITIES = [
  { value: "normal", label: "Normal" },
  { value: "important", label: "Important" },
  { value: "urgent", label: "Urgent" },
];

const priorityStyles = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  important: "bg-amber-100 text-amber-700 border-amber-200",
  normal: "bg-slate-100 text-slate-600 border-slate-200",
};

const categoryStyles = {
  general: "bg-blue-50 text-blue-600",
  policy: "bg-violet-50 text-violet-600",
  event: "bg-teal-50 text-teal-600",
  hr_update: "bg-rose-50 text-rose-600",
  holiday: "bg-green-50 text-green-600",
  achievement: "bg-amber-50 text-amber-600",
};

const Announcements = () => {
  const { currentBusiness } = useAuth();
  
  // Use custom hook for data fetching (with employees for targeting)
  const {
    announcements,
    employees,
    loading,
    stats,
    createAnnouncement,
    deleteAnnouncement,
  } = useAnnouncements(currentBusiness?.id, true);

  // Local UI state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: "",
    content: "",
    category: "general",
    priority: "normal",
    notify_employees: true,
    target_type: "all",
    target_employee_ids: [],
  });

  // Employee search state
  const [empSearch, setEmpSearch] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredEmployees = employees.filter((e) => {
    const name = `${e.first_name || ""} ${e.last_name || ""}`.toLowerCase();
    const email = (e.email || "").toLowerCase();
    const q = empSearch.toLowerCase();
    const isSelected = selectedEmployees.some((s) => s.id === e.id);
    return !isSelected && (name.includes(q) || email.includes(q));
  });

  const addEmployee = (emp) => {
    setSelectedEmployees((prev) => [...prev, emp]);
    setEmpSearch("");
    setShowDropdown(false);
  };

  const removeEmployee = (id) => {
    setSelectedEmployees((prev) => prev.filter((e) => e.id !== id));
  };

  const resetForm = () => {
    setForm({ title: "", content: "", category: "general", priority: "normal", notify_employees: true, target_type: "all", target_employee_ids: [] });
    setSelectedEmployees([]);
    setEmpSearch("");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    if (form.target_type === "selected" && selectedEmployees.length === 0) {
      toast.error("Please select at least one employee");
      return;
    }
    setCreating(true);
    try {
      await createAnnouncement({
        ...form,
        target_employee_ids: form.target_type === "selected" ? selectedEmployees.map((e) => e.id) : [],
      });
      toast.success("Announcement published!");
      resetForm();
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create announcement");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await deleteAnnouncement(id);
      toast.success("Announcement deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const getCategoryLabel = (val) => CATEGORIES.find((c) => c.value === val)?.label || val;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="announcements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Announcements</h1>
          <p className="text-slate-500">Publish company-wide updates and newsletters</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2" data-testid="create-announcement-btn">
              <Plus className="h-4 w-4" />
              New Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-primary" />
                Create Announcement
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="ann-title">Title</Label>
                <Input
                  id="ann-title"
                  placeholder="e.g. Office Closed on Republic Day"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="input-field"
                  data-testid="announcement-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ann-content">Content</Label>
                <Textarea
                  id="ann-content"
                  placeholder="Write the details of your announcement..."
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="input-field min-h-[100px]"
                  data-testid="announcement-content-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="input-field" data-testid="announcement-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="input-field" data-testid="announcement-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Target Audience */}
              <div className="space-y-3">
                <Label>Send To</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setForm({ ...form, target_type: "all" }); setSelectedEmployees([]); }}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                      form.target_type === "all"
                        ? "bg-primary/10 border-primary text-primary"
                        : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                    data-testid="target-all-btn"
                  >
                    <Users className="h-4 w-4" />
                    All Employees
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, target_type: "selected" })}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                      form.target_type === "selected"
                        ? "bg-primary/10 border-primary text-primary"
                        : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                    data-testid="target-selected-btn"
                  >
                    <UserCheck className="h-4 w-4" />
                    Select Employees
                  </button>
                </div>

                {/* Employee Search & Selection */}
                {form.target_type === "selected" && (
                  <div className="space-y-2">
                    {/* Selected chips */}
                    {selectedEmployees.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {selectedEmployees.map((emp) => (
                          <span
                            key={emp.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
                            data-testid={`selected-emp-${emp.id}`}
                          >
                            {emp.first_name} {emp.last_name}
                            <button type="button" onClick={() => removeEmployee(emp.id)} className="hover:text-red-500 ml-0.5">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Search input */}
                    <div className="relative" ref={searchRef}>
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search employees by name or email..."
                        value={empSearch}
                        onChange={(e) => { setEmpSearch(e.target.value); setShowDropdown(true); }}
                        onFocus={() => setShowDropdown(true)}
                        className="input-field pl-9"
                        data-testid="employee-search-input"
                      />
                      {showDropdown && filteredEmployees.length > 0 && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-44 overflow-y-auto" data-testid="employee-search-dropdown">
                          {filteredEmployees.slice(0, 20).map((emp) => (
                            <button
                              key={emp.id}
                              type="button"
                              onClick={() => addEmployee(emp)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors"
                              data-testid={`emp-option-${emp.id}`}
                            >
                              <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-medium text-slate-600">
                                  {emp.first_name?.charAt(0)}{emp.last_name?.charAt(0)}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-700 truncate">{emp.first_name} {emp.last_name}</p>
                                <p className="text-xs text-slate-400 truncate">{emp.email}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {showDropdown && empSearch && filteredEmployees.length === 0 && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm text-slate-400 text-center">
                          No employees found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={form.notify_employees}
                  onChange={(e) => setForm({ ...form, notify_employees: e.target.checked })}
                  className="rounded border-slate-300"
                  data-testid="announcement-notify-checkbox"
                />
                Send notification to {form.target_type === "all" ? "all employees" : "selected employees"}
              </label>
              <Button type="submit" className="w-full btn-primary gap-2" disabled={creating} data-testid="publish-announcement-btn">
                <Send className="h-4 w-4" />
                {creating ? "Publishing..." : "Publish Announcement"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats - using hook's stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-soft">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-100">
              <Megaphone className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-xs text-slate-500">Total Announcements</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-xl bg-red-100">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.urgent}</p>
              <p className="text-xs text-slate-500">Urgent</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-xl bg-amber-100">
              <Info className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.important}</p>
              <p className="text-xs text-slate-500">Important</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Announcements List */}
      {announcements.length === 0 ? (
        <Card className="card-soft">
          <CardContent className="py-16 text-center">
            <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No announcements yet</p>
            <p className="text-sm text-slate-400 mt-1">Create one to keep your team informed</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((a) => (
            <Card key={a.id} className="card-soft hover:shadow-md transition-shadow" data-testid={`announcement-${a.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge variant="outline" className={priorityStyles[a.priority] || priorityStyles.normal}>
                        {a.priority === "urgent" && <AlertTriangle className="h-3 w-3 mr-1" />}
                        {a.priority?.charAt(0).toUpperCase() + a.priority?.slice(1)}
                      </Badge>
                      <Badge variant="outline" className={categoryStyles[a.category] || categoryStyles.general}>
                        <Tag className="h-3 w-3 mr-1" />
                        {getCategoryLabel(a.category)}
                      </Badge>
                      {a.target_type === "selected" && (
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-600 border-indigo-200">
                          <UserCheck className="h-3 w-3 mr-1" />
                          {a.target_employee_names?.length || 0} selected
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-1">{a.title}</h3>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{a.content}</p>
                    {a.target_type === "selected" && a.target_employee_names?.length > 0 && (
                      <p className="text-xs text-indigo-500 mt-2">
                        Sent to: {a.target_employee_names.join(", ")}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {a.author_name || "Admin"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(a.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        {a.target_type === "selected" ? (
                          <><UserCheck className="h-3 w-3" /> Selected</>
                        ) : (
                          <><Users className="h-3 w-3" /> Everyone</>
                        )}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0"
                    onClick={() => handleDelete(a.id)}
                    data-testid={`delete-announcement-${a.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Announcements;
